import { useMemo, type ReactNode } from 'react';

/**
 * Small, dependency-free SVG charts for the Observatoire. Each takes plain
 * numbers and draws with the app's CSS variables; nothing here fetches.
 */

export function Kpi({ value, label, hint, tone }: { value: ReactNode; label: string; hint?: string; tone?: 'ok' | 'warn' | 'err' }) {
  return (
    <div className={`usage-kpi ${tone ? `usage-kpi--${tone}` : ''}`} title={hint}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

export const pct = (x: number | null | undefined, digits = 0) => (x == null ? '—' : `${(x * 100).toFixed(digits)} %`);

/** Horizontal bar list: label, value, bar scaled to the max. */
export function BarList({ rows, max, format, colorOf }: {
  rows: { key: string | number; label: ReactNode; value: number; color?: string | null; sub?: ReactNode }[];
  max?: number;
  format?: (v: number) => string;
  colorOf?: (row: { color?: string | null }) => string;
}) {
  const m = max ?? Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="barlist">
      {rows.map((r) => (
        <div key={r.key} className="barlist-row">
          <div className="barlist-label" title={typeof r.label === 'string' ? r.label : undefined}>{r.label}</div>
          <div className="barlist-track">
            <span className="barlist-bar" style={{ width: `${Math.max(1.5, (r.value / m) * 100)}%`, background: colorOf ? colorOf(r) : r.color ?? 'var(--accent)' }} />
          </div>
          <div className="barlist-value">{format ? format(r.value) : r.value}{r.sub && <span className="barlist-sub">{r.sub}</span>}</div>
        </div>
      ))}
    </div>
  );
}

/** Stacked columns, one per period, segments by series (e.g. colour per month). */
export function StackedColumns({ columns, series, height = 160, labelEvery = 1, valueLabel }: {
  columns: { label: string; values: Record<string, number>; total?: number }[];
  series: { key: string; color: string; name: string }[];
  height?: number;
  labelEvery?: number;
  valueLabel?: (c: { label: string; values: Record<string, number> }) => string;
}) {
  const max = Math.max(1, ...columns.map((c) => series.reduce((s, k) => s + (c.values[k.key] ?? 0), 0)));
  const W = 100 / Math.max(columns.length, 1);
  return (
    <div className="stackcols">
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="stackcols-svg" style={{ height }}>
        {columns.map((c, i) => {
          let y = height - 18;
          return (
            <g key={i}>
              <title>{`${c.label} — ${valueLabel ? valueLabel(c) : series.reduce((s, k) => s + (c.values[k.key] ?? 0), 0)}`}</title>
              {series.map((k) => {
                const v = c.values[k.key] ?? 0;
                const h = (v / max) * (height - 26);
                y -= h;
                return v > 0 ? <rect key={k.key} x={i * W + W * 0.15} y={y} width={W * 0.7} height={h} fill={k.color} /> : null;
              })}
            </g>
          );
        })}
      </svg>
      <div className="stackcols-labels">
        {columns.map((c, i) => <span key={i} style={{ width: `${W}%` }}>{i % labelEvery === 0 ? c.label : ''}</span>)}
      </div>
    </div>
  );
}

export function Legend({ items }: { items: { color: string; name: string }[] }) {
  return (
    <div className="chart-legend">
      {items.map((i) => <span key={i.name} className="chart-legend-item"><i style={{ background: i.color }} />{i.name}</span>)}
    </div>
  );
}

/** Matrix heatmap with row/column labels (values ≥ 0). */
export function Heatmap({ rows, cols, matrix, rowNormalise = false, colors }: {
  rows: string[]; cols: string[]; matrix: number[][]; rowNormalise?: boolean; colors?: (string | null)[];
}) {
  const normalised = rowNormalise
    ? matrix.map((r) => { const s = r.reduce((a, b) => a + b, 0) || 1; return r.map((v) => v / s); })
    : matrix;
  const max = Math.max(1e-9, ...normalised.flat());
  return (
    <div className="heatmap" style={{ gridTemplateColumns: `minmax(120px, 1fr) repeat(${cols.length}, 36px)` }}>
      <div />
      {cols.map((c, j) => <div key={j} className="heatmap-col" title={c}><span style={{ background: colors?.[j] ?? 'transparent' }} /></div>)}
      {rows.map((r, i) => (
        <div key={i} className="heatmap-rowwrap" style={{ display: 'contents' }}>
          <div className="heatmap-row" title={r}><span className="heatmap-dot" style={{ background: colors?.[i] ?? 'var(--ink-3)' }} />{r}</div>
          {cols.map((c, j) => {
            const v = normalised[i][j];
            const raw = matrix[i][j];
            return (
              <div key={j} className="heatmap-cell" style={{ background: `color-mix(in srgb, var(--accent) ${Math.round((v / max) * 85)}%, var(--surface))` }} title={`${r} → ${c} : ${raw}${rowNormalise ? ` (${(v * 100).toFixed(0)} %)` : ''}`}>
                {raw > 0 ? (rowNormalise ? `${Math.round(v * 100)}` : raw) : ''}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Multi-line chart over an integer x axis (e.g. séance index). */
export function Lines({ series, xLabels, yMin = 1, yMax = 5, height = 180, yTicks }: {
  series: { name: string; color: string; points: { x: number; y: number | null; n?: number }[] }[];
  xLabels: string[]; yMin?: number; yMax?: number; height?: number; yTicks?: { v: number; label: string }[];
}) {
  const W = 600, padL = 28, padR = 10, padT = 10, padB = 24;
  const n = xLabels.length;
  const x = (i: number) => padL + ((i) / Math.max(n - 1, 1)) * (W - padL - padR);
  const y = (v: number) => padT + (1 - (v - yMin) / (yMax - yMin)) * (height - padT - padB);
  return (
    <svg viewBox={`0 0 ${W} ${height}`} className="lines-svg" style={{ width: '100%', height }}>
      {(yTicks ?? []).map((t) => (
        <g key={t.v}>
          <line x1={padL} x2={W - padR} y1={y(t.v)} y2={y(t.v)} className="lines-grid" />
          <text x={padL - 6} y={y(t.v) + 3} className="lines-tick" textAnchor="end">{t.label}</text>
        </g>
      ))}
      {xLabels.map((l, i) => <text key={i} x={x(i)} y={height - 6} className="lines-tick" textAnchor="middle">{l}</text>)}
      {series.map((s) => {
        const pts = s.points.filter((p) => p.y != null) as { x: number; y: number; n?: number }[];
        return (
          <g key={s.name} style={{ color: s.color }}>
            {pts.length > 1 && <polyline points={pts.map((p) => `${x(p.x)},${y(p.y)}`).join(' ')} fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" />}
            {pts.map((p) => <circle key={p.x} cx={x(p.x)} cy={y(p.y)} r={p.n != null ? Math.min(6, 2 + Math.sqrt(p.n)) : 3} fill="currentColor" stroke="var(--surface)" strokeWidth={1.5}><title>{`${s.name} · ${xLabels[p.x]} : ${p.y.toFixed(2)}${p.n != null ? ` (n=${p.n})` : ''}`}</title></circle>)}
          </g>
        );
      })}
    </svg>
  );
}

/** Tiny two-series weekly sparkline (held as ghost bars, recorded on top). */
export function Spark({ points, height = 22 }: { points: { held: number; recorded: number }[]; height?: number }) {
  const max = Math.max(1, ...points.map((p) => p.held));
  const w = 100 / Math.max(points.length, 1);
  return (
    <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="spark" style={{ height }}>
      {points.map((p, i) => (
        <g key={i}>
          <rect x={i * w + w * 0.2} y={height - (p.held / max) * height} width={w * 0.6} height={(p.held / max) * height} className="spark-held" />
          <rect x={i * w + w * 0.2} y={height - (p.recorded / max) * height} width={w * 0.6} height={(p.recorded / max) * height} className="spark-rec" />
        </g>
      ))}
    </svg>
  );
}

/** Force-directed graph of cards that go together; deterministic layout. */
export function ForceGraph({ nodes, edges, width = 720, height = 480, labelTop = 40 }: {
  nodes: { id: number; name: string; color: string | null; n: number }[];
  edges: { a: number; b: number; n: number; lift: number }[];
  width?: number; height?: number; labelTop?: number;
}) {
  const layout = useMemo(() => {
    const idx = new Map(nodes.map((n, i) => [n.id, i]));
    const N = nodes.length;
    if (N === 0) return { pos: [] as { x: number; y: number }[] };
    let seed = 7;
    const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
    const pos = nodes.map(() => ({ x: width / 2 + (rnd() - 0.5) * width * 0.6, y: height / 2 + (rnd() - 0.5) * height * 0.6 }));
    const vel = nodes.map(() => ({ x: 0, y: 0 }));
    const links = edges.map((e) => ({ a: idx.get(e.a)!, b: idx.get(e.b)!, w: Math.min(3, Math.max(0.2, e.lift - 0.8)) * Math.log2(1 + e.n) })).filter((l) => l.a != null && l.b != null);
    const k = Math.sqrt((width * height) / N) * 0.9;
    for (let it = 0; it < 320; it++) {
      const t = 1 - it / 320;
      for (let i = 0; i < N; i++) for (let j = i + 1; j < N; j++) {
        let dx = pos[i].x - pos[j].x, dy = pos[i].y - pos[j].y;
        let d2 = dx * dx + dy * dy; if (d2 < 1) { dx = rnd() - 0.5; dy = rnd() - 0.5; d2 = 1; }
        const f = (k * k) / d2 * 0.9;
        const fx = dx * f / Math.sqrt(d2), fy = dy * f / Math.sqrt(d2);
        vel[i].x += fx; vel[i].y += fy; vel[j].x -= fx; vel[j].y -= fy;
      }
      for (const l of links) {
        const dx = pos[l.b].x - pos[l.a].x, dy = pos[l.b].y - pos[l.a].y;
        const d = Math.max(1, Math.sqrt(dx * dx + dy * dy));
        const f = ((d - k * 0.6) / k) * 0.08 * l.w;
        vel[l.a].x += dx / d * f * k; vel[l.a].y += dy / d * f * k; vel[l.b].x -= dx / d * f * k; vel[l.b].y -= dy / d * f * k;
      }
      for (let i = 0; i < N; i++) {
        // gravity to centre
        vel[i].x += (width / 2 - pos[i].x) * 0.01; vel[i].y += (height / 2 - pos[i].y) * 0.01;
        const sp = Math.sqrt(vel[i].x ** 2 + vel[i].y ** 2), maxSp = 12 * t + 1;
        if (sp > maxSp) { vel[i].x *= maxSp / sp; vel[i].y *= maxSp / sp; }
        pos[i].x = Math.min(width - 20, Math.max(20, pos[i].x + vel[i].x)); pos[i].y = Math.min(height - 16, Math.max(16, pos[i].y + vel[i].y));
        vel[i].x *= 0.6; vel[i].y *= 0.6;
      }
    }
    return { pos };
  }, [nodes, edges, width, height]);
  const idx = new Map(nodes.map((n, i) => [n.id, i]));
  const maxN = Math.max(1, ...nodes.map((n) => n.n));
  const labelled = new Set([...nodes].sort((a, b) => b.n - a.n).slice(0, labelTop).map((n) => n.id));
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="force-svg" style={{ width: '100%', maxWidth: width, height: 'auto' }}>
      {edges.map((e, i) => {
        const a = layout.pos[idx.get(e.a)!], b = layout.pos[idx.get(e.b)!];
        if (!a || !b) return null;
        return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} className="force-edge" strokeWidth={Math.min(5, 0.6 + Math.log2(1 + e.n) * Math.max(0.3, e.lift - 0.9))} opacity={Math.min(0.8, 0.15 + e.lift / 6)}><title>{`${nodes[idx.get(e.a)!].name} + ${nodes[idx.get(e.b)!].name} : ${e.n}× (lift ${e.lift})`}</title></line>;
      })}
      {nodes.map((n, i) => {
        const p = layout.pos[i];
        const r = 4 + Math.sqrt(n.n / maxN) * 14;
        return (
          <g key={n.id} transform={`translate(${p.x},${p.y})`}>
            <circle r={r} fill={n.color ?? 'var(--ink-3)'} stroke="var(--surface)" strokeWidth={1.5}><title>{`${n.name} — ${n.n}×`}</title></circle>
            {labelled.has(n.id) && <text y={r + 10} className="force-label" textAnchor="middle">{n.name.length > 22 ? n.name.slice(0, 21) + '…' : n.name}</text>}
          </g>
        );
      })}
    </svg>
  );
}

/** Dot plot of a value between 0 and 1 per row (e.g. mean journey position). */
export function Dots({ rows, leftLabel, rightLabel }: { rows: { label: string; value: number; color?: string | null; n?: number }[]; leftLabel: string; rightLabel: string }) {
  return (
    <div className="dots">
      <div className="dots-axis"><span>{leftLabel}</span><span>{rightLabel}</span></div>
      {rows.map((r) => (
        <div key={r.label} className="dots-row" title={r.n != null ? `${r.label} · n=${r.n}` : r.label}>
          <div className="dots-label">{r.label}</div>
          <div className="dots-track"><span className="dots-dot" style={{ left: `${r.value * 100}%`, background: r.color ?? 'var(--accent)' }} /></div>
        </div>
      ))}
    </div>
  );
}
